/**
 * Phase 8 · C-10 — Live weekly/daily time-series bar chart.
 *
 * Replaces the 7-bar static skeleton with a real TanStack-Query-driven chart
 * that renders the selected metric for the current date range and granularity.
 *
 * Keeps the existing visual shape (flex row of tinted div "bars" + x-axis
 * labels). No chart libraries — pure div primitives matching the SummaryView.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  analyticsWeeklyService,
  type AnalyticsGranularity,
  type AnalyticsMetric,
  type AnalyticsRange,
  type TimeSeriesPoint,
} from '@core/supabase/services/analyticsWeeklyService';
import type { DashboardDateRange } from './DashboardDateRangePicker';

interface WeeklyChartProps {
  orgId: string;
  /** One of the four metrics the service supports. */
  metric: AnalyticsMetric;
  /** Dashboard-wide range picker value (7d / 30d / 90d / 180d / all). */
  range: DashboardDateRange;
  /** User-toggled granularity from the Daily/Weekly switch. */
  granularity: AnalyticsGranularity;
}

/**
 * The service only supports 7d / 30d / 90d. Map the broader dashboard range
 * (which also includes 180d and 'all') to the closest supported bucket.
 */
function toAnalyticsRange(range: DashboardDateRange): AnalyticsRange {
  if (range === '7d') return '7d';
  if (range === '30d') return '30d';
  return '90d';
}

function fmtBucketLabel(iso: string, granularity: AnalyticsGranularity, range: AnalyticsRange): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  if (granularity === 'monthly') {
    return d.toLocaleDateString(undefined, { month: 'short', year: '2-digit', timeZone: 'UTC' });
  }
  if (granularity === 'daily' && range === '7d') {
    return d.toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' });
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

const EMERALD_TINT = 'bg-[#D1FAE5] dark:bg-[#065F46]';

export function WeeklyChart({ orgId, metric, range, granularity }: WeeklyChartProps) {
  const analyticsRange = toAnalyticsRange(range);

  const q = useQuery<TimeSeriesPoint[]>({
    queryKey: ['analyticsWeekly', orgId, metric, analyticsRange, granularity],
    enabled: Boolean(orgId),
    queryFn: () =>
      analyticsWeeklyService.getTimeSeries(orgId, metric, analyticsRange, granularity),
    staleTime: 60_000,
  });

  const series: TimeSeriesPoint[] = useMemo(() => {
    if (q.data && q.data.length > 0) return q.data;
    return analyticsWeeklyService.zeroSeries(analyticsRange, granularity);
  }, [q.data, analyticsRange, granularity]);

  const maxValue = useMemo(
    () => Math.max(1, ...series.map((p) => p.value)),
    [series],
  );

  const isEmpty = !q.isLoading && !q.isError && series.every((p) => p.value === 0);
  const showFallbackLabel = q.isError || isEmpty;

  // Monthly charts have fewer bars so make them taller and wider
  const chartH = granularity === 'monthly' ? 160 : 120;
  const barGap = granularity === 'monthly' ? 'gap-3' : 'gap-2';

  return (
    <div className="relative flex w-full flex-col gap-2" aria-busy={q.isLoading ? 'true' : undefined}>
      <div className={`flex flex-1 items-end ${barGap}`} style={{ minHeight: chartH }}>
        {series.map((p, i) => {
          const heightPx = Math.max(2, Math.round((p.value / maxValue) * chartH));
          return (
            /* Outer slot takes equal flex share; inner bar is capped at 18px wide and centred */
            <div key={`${p.bucket}-${i}`} className="group relative flex flex-1 flex-col items-center gap-1">
              {/* Tooltip on hover */}
              {!q.isLoading && p.value > 0 && (
                <div className="pointer-events-none absolute bottom-full mb-1 hidden rounded bg-gray-800 px-1.5 py-0.5 text-[10px] text-white group-hover:block whitespace-nowrap">
                  {p.value}
                </div>
              )}
              <div
                className={`rounded-t-[4px] transition-[height] duration-300 ${
                  q.isLoading ? 'animate-pulse bg-gray-200 dark:bg-gray-700' : EMERALD_TINT
                }`}
                style={{
                  height: `${q.isLoading ? 48 : heightPx}px`,
                  width: '18px',
                }}
                title={`${fmtBucketLabel(p.bucket, granularity, analyticsRange)} · ${p.value}`}
                aria-hidden
              />
            </div>
          );
        })}
      </div>

      <div className={`flex ${barGap}`}>
        {series.map((p, i) => (
          <div
            key={`${p.bucket}-lbl-${i}`}
            className="flex-1 truncate text-center text-[10px] vscode-text-tertiary"
          >
            {fmtBucketLabel(p.bucket, granularity, analyticsRange)}
          </div>
        ))}
      </div>

      {showFallbackLabel ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="rounded-[10px] border vscode-border vscode-bg-elevated px-2 py-0.5 text-[10px] vscode-text-tertiary">
            {q.isError ? 'No data available' : 'No data'}
          </span>
        </div>
      ) : null}
    </div>
  );
}

export default WeeklyChart;
